import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalDropdownField } from '../../../components/GlobalComponents/GlobalDropdownField';
import LumpSumAllocator, { AllocatorSnapshot, fmtMoney, fmtMonthFull } from './LumpSumAllocator';

interface StudentDisplayInfo {
  student_name?: string;
  class?: string;
  section?: string | null;
  roll_no?: number;
}

interface ProcessedMonth {
  month_year: string;
  total_amount: number;
  paid_amount: number;
  fees_status: string;
  is_settled?: boolean;
  settlement_discount?: number;
  late_fee_exempted?: boolean;
  late_fee_waived?: number;
}

interface LumpSumFeeModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string | null;
  sessionId: string | null;
  session?: string | null;
  sessionStart?: string | null;
  sessionEnd?: string | null;
  classId?: string | null;
  schoolId?: string;
  studentInfo?: StudentDisplayInfo | null;
  onSuccess: () => void;
  toast: React.RefObject<Toast | null>;
}

const PAY_MODE_OPTIONS = [
  { label: 'Cash', value: 'cash' },
  { label: 'Online', value: 'online' },
  { label: 'Cheque', value: 'cheque' },
];

const EMPTY_SNAPSHOT: AllocatorSnapshot = {
  loading: true,
  chargeableMonths: [],
  optionalFeeSelections: {},
  includeLateFee: false,
  monthlyLateFees: {},
  monthlyLateDays: {},
  mandatoryTotal: 0,
  optionalTotal: 0,
  lateTotal: 0,
  estimatedTotal: 0,
  coverage: null,
};

const Card = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      background: 'rgba(255,255,255,0.8)',
      borderRadius: 16,
      padding: 20,
      border: '1px solid rgba(59,130,246,0.1)',
      boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    }}
  >
    {children}
  </div>
);

const LumpSumFeeModal: React.FC<LumpSumFeeModalProps> = ({
  open,
  onClose,
  studentId,
  sessionId,
  session,
  sessionStart,
  sessionEnd,
  classId,
  schoolId,
  studentInfo,
  onSuccess,
  toast,
}) => {
  const [paidAmount, setPaidAmount] = useState('');
  const [payMode, setPayMode] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [result, setResult] = useState<{
    processedMonths: ProcessedMonth[];
    totalPaid: number;
    remainingPool: number;
    settlementDiscount: number;
    isSettlement: boolean;
  } | null>(null);

  // One-time settlement — the agreed amount clears every chargeable month;
  // the shortfall is stored per-record as settlement_discount.
  const [isSettlement, setIsSettlement] = useState(false);
  const [settlementReason, setSettlementReason] = useState('');

  // Everything the allocator knows, reported up on every change.
  const [snapshot, setSnapshot] = useState<AllocatorSnapshot>(EMPTY_SNAPSHOT);
  const handleSnapshot = useCallback((s: AllocatorSnapshot) => setSnapshot(s), []);

  useEffect(() => {
    if (!open) {
      setPaidAmount('');
      setPayMode('cash');
      setResult(null);
      setIsSettlement(false);
      setSettlementReason('');
      setSnapshot(EMPTY_SNAPSHOT);
    }
  }, [open]);

  useEffect(() => {
    if (isClosing) {
      document.querySelector('.lump-sum-dialog')?.classList.add('dialog-closing');
      document.querySelector('.lump-sum-dialog-mask')?.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    if (loading) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const { chargeableMonths, estimatedTotal, coverage, includeLateFee, monthlyLateFees } = snapshot;
  const hasLate = Object.keys(monthlyLateFees).some(my => chargeableMonths.includes(my) && monthlyLateFees[my] > 0);

  const parsedAmount = parseFloat(paidAmount);
  const amountInvalid = paidAmount.trim() !== '' && (isNaN(parsedAmount) || parsedAmount <= 0);

  // Normal payment: can't exceed the outstanding total.
  // Settlement: must be strictly less than it — otherwise it's a normal payment.
  const amountExceedsMax =
    !amountInvalid &&
    paidAmount.trim() !== '' &&
    estimatedTotal > 0 &&
    (isSettlement ? parsedAmount >= estimatedTotal : parsedAmount > estimatedTotal);

  const settlementDiscount =
    isSettlement && !amountInvalid && !amountExceedsMax && parsedAmount > 0
      ? Math.round((estimatedTotal - parsedAmount) * 100) / 100
      : 0;

  const amountErrorMessage = amountInvalid ? 'Enter an amount greater than 0 to continue.' : '';

  const handleSubmit = async () => {
    if (!studentId || !sessionId) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Missing student or session', life: 4000 });
      return;
    }
    const amount = parseFloat(paidAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.current?.show({ severity: 'warn', summary: 'Invalid amount', detail: 'Enter a valid amount greater than 0', life: 3000 });
      return;
    }
    if (estimatedTotal > 0 && (isSettlement ? amount >= estimatedTotal : amount > estimatedTotal)) {
      toast.current?.show({
        severity: 'warn',
        summary: isSettlement ? 'Not a settlement' : 'Amount too high',
        detail: isSettlement
          ? `A settlement must be less than the outstanding total of ${fmtMoney(estimatedTotal)}.`
          : `Amount can't exceed the total outstanding of ${fmtMoney(estimatedTotal)}`,
        life: 4000,
      });
      return;
    }

    const body: Record<string, any> = {
      session_id: sessionId,
      student_id: studentId,
      paid_amount: amount,
      pay_mode: payMode,
      // Always send late fees; backend charges them only when include_late_fee
      // is true, otherwise stores late_fee_exempted + the waived amount.
      include_late_fee: snapshot.includeLateFee,
      monthly_late_fees: snapshot.monthlyLateFees,
      monthly_late_days: snapshot.monthlyLateDays,
    };
    if (schoolId) body.school_id = schoolId;
    if (Object.keys(snapshot.optionalFeeSelections).length > 0) {
      body.optional_fee_selections = snapshot.optionalFeeSelections;
    }
    if (isSettlement) {
      body.is_settlement = true;
      if (settlementReason.trim()) body.settlement_reason = settlementReason.trim();
    }

    setLoading(true);
    try {
      const res = await apiClient.post('/api/fee-records/lump-sum', body);
      if (res.data.status === 'success') {
        setResult({
          processedMonths: res.data.processed_months || [],
          totalPaid: res.data.total_paid ?? amount,
          remainingPool: res.data.remaining_pool ?? 0,
          settlementDiscount: res.data.settlement_discount ?? 0,
          isSettlement,
        });
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: res.data.message || 'Lump sum payment allocated successfully.',
          life: 4000,
        });
        onSuccess();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message || 'Failed to allocate lump sum payment', life: 5000 });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to allocate lump sum payment',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const studentName = studentInfo?.student_name || 'Student';
  const classLabel = studentInfo?.class
    ? `${studentInfo.class}${
        studentInfo.section ? ` · Sec ${String(studentInfo.section).toUpperCase()}` : ''
      }${studentInfo.roll_no != null ? ` · Roll ${studentInfo.roll_no}` : ''}`
    : null;

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div
        style={{
          width: 54, height: 54, borderRadius: 14,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontSize: 22, boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
          flexShrink: 0, border: '3px solid white',
        }}
      >
        <i className="pi pi-wallet" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: 4 }}>
          {isSettlement ? 'One-Time Settlement' : 'Pay Lump Sum'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{studentName}</div>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .lump-sum-dialog-mask {
          backdrop-filter: blur(4px);
          background-color: rgba(0, 0, 0, 0.25) !important;
          animation: lsMaskIn 0.35s ease;
        }
        .lump-sum-dialog-mask.mask-closing { animation: lsMaskOut 0.4s ease !important; }
        @keyframes lsMaskIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes lsMaskOut { from { opacity: 1; } to { opacity: 0; } }
        .lump-sum-dialog {
          animation: lsDialogIn 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        .lump-sum-dialog.dialog-closing {
          animation: lsDialogOut 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }
        @keyframes lsDialogIn {
          from { opacity: 0; transform: scale(0.92) translateY(-20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes lsDialogOut {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to   { opacity: 0; transform: scale(0.88) translateY(25px); }
        }
        .lump-sum-month-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 14px; border-radius: 10px;
          background: rgba(248,250,252,0.9);
          border: 1px solid rgba(226,232,240,0.8);
        }
      `}</style>

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        className="lump-sum-dialog"
        maskClassName="lump-sum-dialog-mask"
        style={{ width: '90vw', maxWidth: 960, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255,255,255,0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255,255,255,0.8)',
          borderRight: '12px solid rgba(255,255,255,0.8)',
          borderTop: '12px solid rgba(255,255,255,0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'Name', value: studentName },
                { label: 'Class', value: classLabel ?? 'N/A' },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>{label}:</span>
                  <span style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{value}</span>
                </div>
              ))}
            </div>
          </Card>

          {!result ? (
            <>
              {open && (
                <LumpSumAllocator
                  studentId={studentId}
                  sessionId={sessionId}
                  session={session}
                  sessionStart={sessionStart}
                  sessionEnd={sessionEnd}
                  classId={classId}
                  schoolId={schoolId}
                  pool={isSettlement || amountInvalid ? null : parsedAmount}
                  disabled={loading}
                  tableTitle="Amount To Pay — Month-wise Receipt"
                  toast={toast}
                  onSnapshot={handleSnapshot}
                  onDataChanged={onSuccess}
                />
              )}

              {!snapshot.loading && chargeableMonths.length > 0 && (
                <>
                  {/* ── One-time settlement ── */}
                  <Card>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isSettlement}
                        onChange={e => setIsSettlement(e.target.checked)}
                        disabled={loading}
                      />
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#047857' }}>
                        One-time settlement (agreed reduced amount)
                      </span>
                    </label>
                    {isSettlement && (
                      <>
                        <div
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12,
                            padding: '10px 14px', borderRadius: 10,
                            background: '#ecfdf5', border: '1px solid #a7f3d0',
                          }}
                        >
                          <i className="pi pi-info-circle" style={{ fontSize: 13, color: '#047857', marginTop: 1 }} />
                          <span style={{ fontSize: 12, color: '#065f46', fontWeight: 500, lineHeight: 1.45 }}>
                            The amount below is accepted as full and final for all{' '}
                            <strong>{chargeableMonths.length}</strong> chargeable month
                            {chargeableMonths.length > 1 ? 's' : ''}. Every month is marked paid, the amount is split
                            across their receipts in proportion to what each one owes, and the shortfall is stored as a
                            settlement discount.
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                          {chargeableMonths.map(my => (
                            <span key={my} className="lump-sum-chip settle">
                              {fmtMonthFull(my)}
                            </span>
                          ))}
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <GlobalInputField
                            value={settlementReason}
                            onChange={e => setSettlementReason(e.target.value)}
                            placeholder="Settlement reason / agreement note"
                            disabled={loading}
                          />
                        </div>
                      </>
                    )}
                  </Card>

                  <div
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '10px 14px', borderRadius: 10,
                      background: isSettlement ? '#ecfdf5' : '#eff6ff',
                      border: `1px solid ${isSettlement ? '#a7f3d0' : '#bfdbfe'}`,
                    }}
                  >
                    <i className="pi pi-info-circle" style={{ fontSize: 13, color: isSettlement ? '#047857' : '#1e40af', marginTop: 1 }} />
                    <span style={{ fontSize: 12, color: isSettlement ? '#065f46' : '#1e3a8a', fontWeight: 500, lineHeight: 1.4 }}>
                      {isSettlement
                        ? 'Settlement clears every chargeable month at once — nothing is left partially paid.'
                        : "The amount is auto-allocated chronologically across this student's remaining unpaid months, starting with the earliest."}
                    </span>
                  </div>

                  <Card>
                    <GlobalInputField
                      label={isSettlement ? 'Agreed settlement amount' : 'Amount to pay'}
                      value={paidAmount}
                      onChange={e => setPaidAmount(e.target.value)}
                      placeholder="Enter amount"
                      type="number"
                      required
                      icon="pi pi-indian-rupee"
                      disabled={loading}
                      error={amountErrorMessage}
                    />

                    {amountExceedsMax && (
                      <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca' }}>
                        <div style={{ fontSize: 12, color: '#991b1b', fontWeight: 600, lineHeight: 1.5 }}>
                          {isSettlement
                            ? `A settlement has to be less than the outstanding ${fmtMoney(estimatedTotal)}. Untick settlement to record this as a normal payment.`
                            : `Amount can't exceed the total outstanding of ${fmtMoney(estimatedTotal)} (mandatory + selected optional${includeLateFee ? ' + late fee' : ''}).`}
                        </div>
                      </div>
                    )}

                    {isSettlement && settlementDiscount > 0 && (
                      <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: '#ecfdf5', border: '1px solid #a7f3d0' }}>
                        <div style={{ fontSize: 12, color: '#065f46', fontWeight: 600, lineHeight: 1.5 }}>
                          Settles <strong>{chargeableMonths.length}</strong> month{chargeableMonths.length > 1 ? 's' : ''} for{' '}
                          {fmtMoney(parsedAmount)} — <strong>{fmtMoney(settlementDiscount)}</strong> waived as settlement discount
                          {hasLate && !includeLateFee ? ' (late fees waived separately)' : ''}.
                        </div>
                      </div>
                    )}

                    {!isSettlement && coverage && !amountExceedsMax && (
                      <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, lineHeight: 1.5 }}>
                          {coverage.fullMonths.length > 0 && (
                            <>
                              Covers <strong>{coverage.fullMonths.length}</strong> full month
                              {coverage.fullMonths.length > 1 ? 's' : ''} ({fmtMonthFull(coverage.fullMonths[0])}
                              {coverage.fullMonths.length > 1 ? ` – ${fmtMonthFull(coverage.fullMonths[coverage.fullMonths.length - 1])}` : ''}
                              ){coverage.partial ? ',' : '.'}
                            </>
                          )}
                          {coverage.partial && (
                            <>
                              {' '}plus part of <strong>{fmtMonthFull(coverage.partial.month)}</strong> (
                              {fmtMoney(coverage.partial.paid)} of {fmtMoney(coverage.partial.total)}).
                            </>
                          )}
                          {coverage.fullMonths.length === 0 && !coverage.partial && <>Not enough to cover any month yet.</>}
                        </div>
                        {coverage.leftover > 0 && (
                          <div style={{ fontSize: 11, color: '#166534', marginTop: 4 }}>
                            {fmtMoney(coverage.leftover)} would be left unallocated (no more unpaid months to apply it to).
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ marginTop: 16 }}>
                      <GlobalDropdownField
                        label="Payment mode"
                        value={payMode}
                        onChange={setPayMode}
                        options={PAY_MODE_OPTIONS}
                        placeholder="Select mode"
                        disabled={loading}
                      />
                    </div>
                  </Card>

                  <div style={{ textAlign: 'center' }}>
                    <GlobalButton
                      label={loading ? 'Processing...' : isSettlement ? 'Settle & Mark Paid' : 'Pay Lump Sum'}
                      onClick={handleSubmit}
                      variant="primary"
                      size="medium"
                      disabled={loading || !sessionId || !studentId || !paidAmount.trim() || parseFloat(paidAmount) <= 0 || amountExceedsMax}
                      loading={loading}
                      fullWidth={false}
                    />
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <Card>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a8a', marginBottom: 12 }}>
                  {result.isSettlement ? 'Settlement Summary' : 'Allocation Summary'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {result.processedMonths.length === 0 ? (
                    <span style={{ fontSize: 13, color: '#64748b' }}>No unpaid months were available to allocate against.</span>
                  ) : (
                    result.processedMonths.map(m => (
                      <div key={m.month_year} className="lump-sum-month-row">
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{fmtMonthFull(m.month_year)}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>
                            {m.fees_status}
                            {m.is_settled ? ' · settled' : ''}
                            {m.late_fee_exempted ? ' · late fee waived' : ''}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>
                            {fmtMoney(m.paid_amount)} / {fmtMoney(m.total_amount)}
                          </span>
                          {(m.settlement_discount ?? 0) > 0 && (
                            <div style={{ fontSize: 11, color: '#047857', fontWeight: 600 }}>{fmtMoney(m.settlement_discount!)} waived</div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Card>

              <div style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', borderRadius: 14, padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>Total paid</span>
                  <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>{fmtMoney(result.totalPaid)}</span>
                </div>
                {result.isSettlement && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#a7f3d0', fontWeight: 600 }}>Settlement discount</span>
                    <span style={{ fontSize: 13, color: '#a7f3d0', fontWeight: 700 }}>{fmtMoney(result.settlementDiscount)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>Remaining (unallocated)</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: 'white' }}>{fmtMoney(result.remainingPool)}</span>
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <GlobalButton label="Done" onClick={handleClose} variant="primary" size="medium" fullWidth={false} />
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default LumpSumFeeModal;